/**
 * Enhanced Gemini OCR Service
 * Primary OCR service using Google Gemini API for intelligent certificate data extraction
 * Includes institution name recognition and comprehensive field extraction
 */

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent';

/**
 * Extract complete certificate data using Gemini Vision API
 * This is the PRIMARY OCR method for the application
 */
export async function extractCertificateWithGemini(imageFile) {
  if (!GEMINI_API_KEY) {
    throw new Error('Gemini API key not configured. Please set REACT_APP_GEMINI_API_KEY');
  }

  try {
    // Convert image to base64
    const base64Image = await fileToBase64(imageFile);
    const mimeType = imageFile.type || 'image/jpeg';

    const prompt = createCertificateExtractionPrompt();

    const requestBody = {
      contents: [
        {
          parts: [
            { text: prompt },
            {
              inline_data: {
                mime_type: mimeType,
                data: base64Image
              }
            }
          ]
        }
      ],
      generationConfig: {
        temperature: 0.1,
        topK: 32,
        topP: 1,
        maxOutputTokens: 2048,
      }
    };

    const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Gemini API error: ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    
    if (!data.candidates || !data.candidates[0] || !data.candidates[0].content) {
      throw new Error('Invalid response from Gemini API');
    }

    const extractedText = data.candidates[0].content.parts[0].text;

    // Parse JSON response
    let jsonText = extractedText.trim();
    jsonText = jsonText.replace(/^```json\n?|```$/g, '');
    
    const certificateData = JSON.parse(jsonText);
    
    if (certificateData.error) {
      throw new Error(`Certificate processing failed: ${certificateData.reason || 'Unknown error'}`);
    }

    // Process and normalize the data
    const processedData = processCertificateData(certificateData);
    
    console.log('📄 Extracted Data:', processedData);
    
    return processedData;

  } catch (error) {
    console.error('Gemini extraction failed:', error.message);
    throw error;
  }
}

/**
 * Create comprehensive prompt for certificate extraction
 */
function createCertificateExtractionPrompt() {
  return `You are an expert OCR system for educational and professional certificates.

Extract ALL visible information from this certificate image and return ONLY a JSON object with this structure:

{
  "institution": "Full organization/university name",
  "studentName": "Full name of recipient",
  "rollNumber": "Registration/Serial/Certificate number",
  "certificateType": "Type of certificate",
  "program": "Course/Program/Certification name",
  "issueDate": "Issue date",
  "marks": "CGPA/Score/Grade",
  "batch": "Academic year/Validity period",
  "verificationCode": "Verification/Serial number",
  "additionalInfo": "Any other relevant details"
}

CRITICAL INSTITUTION EXTRACTION:
1. Look at the TOP/HEADER of the certificate for organization name
2. Check near LOGOS and OFFICIAL SEALS
3. For academic: Extract full university/college name (e.g., "Indian Institute of Technology Delhi", "Anna University")
4. For professional: Extract company name (e.g., "Alibaba Cloud", "Google Cloud", "Microsoft", "AWS")
5. Include the COMPLETE official name, not abbreviations
6. This is the MOST IMPORTANT field - extract carefully

FIELD-SPECIFIC RULES:
- institution: NEVER empty unless no org name exists. Look carefully at headers/logos.
- studentName: The recipient's full name (most prominent personal name)
- rollNumber: May be labeled as Serial Number, Certificate ID, Registration No., Enrollment No.
- certificateType: Be specific - "Degree Certificate", "Professional Certification", "Cloud Certification"
- program: Full course/certification name
- issueDate: Use format visible in image (DD/MM/YYYY, Month YYYY, etc.)
- marks: Include unit (8.5 CGPA, 85%, Grade A)
- batch: Academic year or validity period
- verificationCode: Any unique identifier for verification
- additionalInfo: Honors, distinctions, issuing authority, etc.

EXTRACTION RULES:
1. Extract ONLY what is clearly visible - no assumptions
2. Use empty string "" for fields not found (NOT "Not Found" or "N/A")
3. Be precise with names and numbers
4. For institution, check multiple locations: header, footer, logos, seals
5. Return ONLY valid JSON - no markdown, no extra text

If the image is not a certificate or completely unreadable:
{
  "error": "Unable to process",
  "reason": "Image is not a certificate / Image is unreadable"
}`;
}

/**
 * Process and normalize extracted certificate data
 */
function processCertificateData(rawData) {
  // Clean and normalize all fields
  const processed = {
    institution: cleanField(rawData.institution),
    studentName: cleanField(rawData.studentName),
    rollNumber: cleanField(rawData.rollNumber),
    certificateType: cleanField(rawData.certificateType),
    program: cleanField(rawData.program),
    issueDate: cleanField(rawData.issueDate),
    marks: cleanField(rawData.marks),
    batch: cleanField(rawData.batch),
    verificationCode: cleanField(rawData.verificationCode),
    additionalInfo: cleanField(rawData.additionalInfo),
    
    // Metadata
    extractionMethod: 'gemini_vision',
    extractionTimestamp: new Date().toISOString(),
    confidence: 'high'
  };

  return processed;
}

/**
 * Clean individual field
 */
function cleanField(value) {
  if (!value || value === 'Not Found' || value === 'N/A' || value === 'null') {
    return '';
  }
  return typeof value === 'string' ? value.trim() : String(value).trim();
}

/**
 * Convert file to base64
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Test Gemini API connection
 */
export async function testGeminiConnection() {
  if (!GEMINI_API_KEY) {
    return { success: false, error: 'API key not configured' };
  }

  try {
    const testRequest = {
      contents: [
        {
          parts: [
            { text: "Respond with: 'Connection successful'" }
          ]
        }
      ]
    };

    const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(testRequest)
    });

    if (response.ok) {
      return { success: true, message: 'Gemini API is accessible' };
    } else {
      throw new Error(`HTTP ${response.status}`);
    }
  } catch (error) {
    console.error('Gemini API connection failed:', error.message);
    return { success: false, error: error.message };
  }
}

export default {
  extractCertificateWithGemini,
  testGeminiConnection
};
